import { useLang } from 'hooks/lang';

import { Cost, ProjectService, User, getServiceName, loaders } from 'entities';

import { Row, Skeleton, IconButton, Box, ResponsiveOverlay } from 'components/Blocks';
import { ArrowUpIcon, BadgeDollarIcon, CheckIcon, EarthIcon } from 'components/Icons';
import { UserAvatarGroup } from 'components/UserAvatar';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';
import { useToggleV2 } from 'hooks';

import { formatCurrency } from 'utils/text';
import { listGet, toTruthyList } from 'utils/array';
import { formatDate } from 'utils/date';
import { getCountryLabel } from 'utils/countries';

interface CostsProps {
  children?: never;
  service: ProjectService;
  cost: Cost;
  country: string;
}

type HistoryCost = {
  id: string;
  value: string;
  currency?: string;
  date?: string;
  finishedBy?: User;
  repricedBy?: User;
  requestedBy?: User;
};

function addActionToName(user: User | undefined, action: string): User | undefined {
  if (!user) return;

  return {
    ...user,
    name: `${action}: ${user.name}`,
  };
}

export function CostHistory({ cost, service, country }: CostsProps) {
  const { project } = useProjectProfile();
  const { t } = useLang();

  const [isOpen, openHandlers] = useToggleV2();

  const { data, loading } = loaders.projects.allProjectsReprices.use({
    params: { projectId: project.id },

    defaultValue: [],

    blockLoad: !isOpen || !cost.history?.length,

    refreshMinutes: 10,

    parser: (reprices) => reprices.filter((x) => x.finished).reverse(),
  });

  if (!cost.history?.length) return null;

  const costHistory = [
    ...cost.history!.map((history) => {
      const repriceInfo = data.find(({ old, new: newValues }) => {
        const oldService = old.find(
          ({ id, costs }) =>
            id === service.id && listGet(costs[country], history.id)?.value === history.value,
        );

        const newService = newValues?.find(
          ({ id, costs }) =>
            id === service.id && listGet(costs[country], history.id)?.value !== history.value,
        );

        return oldService && newService;
      });

      if (!repriceInfo) return history;

      return {
        ...history,
        date: repriceInfo.finishedAt!,
        finishedBy: addActionToName(repriceInfo.finishedBy, t('finished-by')),
        repricedBy: addActionToName(repriceInfo.repricedBy, t('repriced-by')),
        requestedBy: addActionToName(repriceInfo.requestedBy, t('requested-by')),
      };
    }),
    cost,
  ].reverse() as HistoryCost[];

  return (
    <>
      <ResponsiveOverlay
        default="sheet"
        title={t('history')}
        isOpen={isOpen}
        close={openHandlers.turnOff}
      >
        <Row gap="2">
          <span className="font-medium">{getServiceName(service.id, project.tasks)}</span>

          <EarthIcon className="ml-auto" />
          {getCountryLabel(country)}
        </Row>

        {costHistory.map(
          ({ currency, value, date, finishedBy, repricedBy, requestedBy, id }, index) => (
            <Row gap="2" justifyContent="space-between" key={id}>
              <Row gap="2" className={index ? '' : 'text-primary'}>
                <Box size={24}>{index ? <ArrowUpIcon /> : <CheckIcon />}</Box>

                {currency ? formatCurrency(value, currency) : value}
              </Row>

              {date && (
                <Row gap="2">
                  <UserAvatarGroup
                    users={toTruthyList([finishedBy, repricedBy, requestedBy])}
                    size={22}
                    // spacing="small"
                    avatarClassName="text-sm"
                  />

                  <span className="text-xs">{formatDate(date)}</span>
                </Row>
              )}

              {loading && !!index && <Skeleton width="80px" height={24} />}
            </Row>
          ),
        )}
      </ResponsiveOverlay>

      <IconButton onClick={openHandlers.turnOn} tooltipContent={t('repriced')} size="small">
        <BadgeDollarIcon size={14} color="primary" />
      </IconButton>
    </>
  );
}
